import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Loader2, Trash2, Archive, ArchiveRestore, X, Mail, Phone, FileDown, Send,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Save, MessageSquarePlus, FileSpreadsheet, ChevronDown,
} from "lucide-react";
import {
  API, abs, STATUS, STATUS_ORDER, fmtDate, fmtDay, toDateInput, deadlineColor, DEADLINE_DOT, DEADLINE_LABEL,
  downloadXlsx, todayStr,
} from "./shared";

const VIEWS = [
  { value: "active", label: "Aktívne" },
  { value: "in_progress", label: "Prebieha" },
  { value: "completed", label: "Dokončené" },
  { value: "archived", label: "Archivované" },
  { value: "all", label: "Všetky" },
];
const SORT_COLS = [
  { key: "request_number", label: "Číslo" },
  { key: "date", label: "Dátum" },
  { key: "customer", label: "Zákazník" },
  { key: "status", label: "Stav" },
  { key: "expected_completion", label: "Termín" },
];

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.new;
  return <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${s.cls}`}>{s.label}</span>;
}

function DeadlineDot({ item }) {
  const c = deadlineColor(item);
  if (!c) return <span className="text-white/30 text-xs">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5" title={DEADLINE_LABEL[c]}>
      <span className={`w-2.5 h-2.5 rounded-full ${DEADLINE_DOT[c]}`} />
      <span className="text-white/60 text-xs">{fmtDay(item.expected_completion_date)}</span>
    </span>
  );
}

function RequestDetailModal({ item, token, onClose, onChanged }) {
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const [status, setStatus] = useState(item.status);
  const [notes, setNotes] = useState(item.internal_notes || "");
  const [notesState, setNotesState] = useState("saved"); // saved | saving
  const [dateReceived, setDateReceived] = useState(toDateInput(item.date_received));
  const [expected, setExpected] = useState(toDateInput(item.expected_completion_date));
  const [comms, setComms] = useState(item.communications || []);
  const [newComm, setNewComm] = useState("");
  const [addingComm, setAddingComm] = useState(false);
  const [emailSent, setEmailSent] = useState(!!item.completion_email_sent);
  const [emailSentAt, setEmailSentAt] = useState(item.completion_email_sent_at || null);
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const notesTimer = useRef(null);
  const firstNotes = useRef(true);

  // auto-save internal notes (debounced)
  useEffect(() => {
    if (firstNotes.current) { firstNotes.current = false; return; }
    setNotesState("saving");
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      try {
        await axios.patch(`${API}/admin/requests/${item.id}`, { internal_notes: notes }, authHeader);
        setNotesState("saved");
      } catch { setNotesState("saved"); toast.error("Uloženie poznámky zlyhalo."); }
    }, 800);
    return () => clearTimeout(notesTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  const changeStatus = async (s) => {
    setStatus(s);
    try {
      await axios.patch(`${API}/admin/requests/${item.id}/status`, { status: s }, authHeader);
      toast.success("Stav aktualizovaný");
      onChanged();
    } catch { toast.error("Zmena stavu zlyhala."); setStatus(item.status); }
  };

  const saveDate = async (field, value) => {
    try {
      await axios.patch(`${API}/admin/requests/${item.id}`, { [field]: value || null }, authHeader);
      onChanged();
    } catch { toast.error("Uloženie termínu zlyhalo."); }
  };

  const addComm = async () => {
    const t = newComm.trim();
    if (!t) return;
    setAddingComm(true);
    try {
      const { data } = await axios.post(`${API}/admin/requests/${item.id}/communications`, { text: t }, authHeader);
      setComms((c) => [...c, data]); setNewComm("");
    } catch { toast.error("Pridanie záznamu zlyhalo."); }
    finally { setAddingComm(false); }
  };

  const delComm = async (cid) => {
    try {
      await axios.delete(`${API}/admin/requests/${item.id}/communications/${cid}`, authHeader);
      setComms((c) => c.filter((x) => x.id !== cid));
    } catch { toast.error("Zmazanie záznamu zlyhalo."); }
  };

  const del = async () => {
    if (!window.confirm(`Naozaj zmazať požiadavku ${item.request_number}?`)) return;
    try {
      await axios.delete(`${API}/admin/requests/${item.id}`, authHeader);
      toast.success("Požiadavka zmazaná"); onChanged(); onClose();
    } catch { toast.error("Mazanie zlyhalo."); }
  };

  const sendCompletionEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/admin/requests/${item.id}/completion-email`, { force: emailSent }, authHeader);
      toast.success("E-mail bol odoslaný zákazníkovi.");
      setEmailSent(true); setEmailSentAt(data.sent_at); setConfirmEmail(false); onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        const at = d?.sent_at; setEmailSent(true); if (at) setEmailSentAt(at);
        toast.info(at ? `E-mail už bol odoslaný dňa ${fmtDate(at)}.` : "E-mail už bol odoslaný.");
        setConfirmEmail(false);
      } else { toast.error(typeof d === "string" ? d : (d?.message || "Odoslanie e-mailu zlyhalo.")); }
    } finally { setSendingEmail(false); }
  };

  const dc = deadlineColor({ ...item, expected_completion_date: expected, archived: item.archived });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8 overflow-y-auto" data-testid="request-detail-modal">
      <div className="w-full max-w-lg bg-white text-navy rounded-2xl shadow-2xl relative my-auto">
        <div className="flex items-start justify-between p-6 border-b border-navy/10">
          <div>
            <p className="font-mono text-sm text-brand font-semibold">{item.request_number}</p>
            <p className="text-xs text-slate-400 mt-1">{fmtDate(item.created_at)} · {item.source === "chatbot" ? "Chatbot" : "Formulár"}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="request-detail-close" className="text-slate-400 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          <div className="flex items-center gap-3">
            <span className="text-slate-500">Stav:</span>
            <select data-testid="request-status-select" value={status} onChange={(e) => changeStatus(e.target.value)}
              className="px-3 py-1.5 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand bg-white">
              {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-y-3 gap-x-2">
            <span className="text-slate-500">Zákazník</span><span className="col-span-2 font-medium">{item.name}</span>
            <span className="text-slate-500">Telefón</span>
            <a href={`tel:${item.phone}`} className="col-span-2 font-medium text-brand hover:underline inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{item.phone}</a>
            <span className="text-slate-500">E-mail</span>
            {item.email ? <a href={`mailto:${item.email}`} className="col-span-2 font-medium text-brand hover:underline inline-flex items-center gap-1 break-all"><Mail className="w-3.5 h-3.5 shrink-0" />{item.email}</a> : <span className="col-span-2 text-slate-400">—</span>}
            <span className="text-slate-500">Služba</span><span className="col-span-2 font-medium">{item.service_type}</span>
            <span className="text-slate-500">Zariadenie</span><span className="col-span-2 font-medium">{item.device || "—"}</span>
          </div>

          {/* Scheduling */}
          <div className="border-t border-navy/10 pt-4" data-testid="scheduling-section">
            <p className="text-slate-500 mb-2 flex items-center gap-2">
              Termíny
              {dc && <span className="inline-flex items-center gap-1.5 text-xs"><span className={`w-2.5 h-2.5 rounded-full ${DEADLINE_DOT[dc]}`} />{DEADLINE_LABEL[dc]}</span>}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-500">Dátum prijatia</span>
                <input data-testid="date-received" type="date" value={dateReceived}
                  onChange={(e) => { setDateReceived(e.target.value); saveDate("date_received", e.target.value); }}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand" />
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Predpokladané dokončenie</span>
                <input data-testid="expected-completion" type="date" value={expected}
                  onChange={(e) => { setExpected(e.target.value); saveDate("expected_completion_date", e.target.value); }}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand" />
              </label>
            </div>
          </div>

          <div>
            <p className="text-slate-500 mb-1">Správa zákazníka</p>
            <p className="bg-slate-50 border border-navy/10 rounded-md p-3 whitespace-pre-wrap">{item.description || "—"}</p>
          </div>

          {item.attachments && item.attachments.length > 0 && (
            <div>
              <p className="text-slate-500 mb-2">Prílohy</p>
              <div className="flex flex-col gap-2">
                {item.attachments.map((a, i) => (
                  <a key={a.url || a.filename || i} data-testid={`request-attachment-${i}`} href={abs(a.url)} target="_blank" rel="noopener noreferrer" download={a.filename}
                    className="inline-flex items-center gap-2 text-sm text-brand hover:underline border border-navy/10 rounded-md px-3 py-2 bg-slate-50">
                    <FileDown className="w-4 h-4" /> {a.filename}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Internal notes */}
          <div className="border-t border-navy/10 pt-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-slate-500">Interné poznámky <span className="text-xs text-slate-400">(len pre administrátorov)</span></p>
              <span className="text-xs text-slate-400 inline-flex items-center gap-1">
                {notesState === "saving" ? <><Loader2 className="w-3 h-3 animate-spin" /> Ukladá sa…</> : <><Save className="w-3 h-3" /> Uložené</>}
              </span>
            </div>
            <textarea data-testid="internal-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder="Súkromné poznámky, nikdy nie sú viditeľné pre zákazníka…"
              className="w-full px-3 py-2 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand resize-y" />
          </div>

          {/* Communication history */}
          <div className="border-t border-navy/10 pt-4" data-testid="communication-section">
            <p className="text-slate-500 mb-2">História komunikácie <span className="text-xs text-slate-400">(interné)</span></p>
            {comms.length > 0 && (
              <div className="space-y-2 mb-3">
                {comms.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((c) => (
                  <div key={c.id} data-testid={`comm-${c.id}`} className="group bg-slate-50 border border-navy/10 rounded-md p-2.5 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="whitespace-pre-wrap break-words">{c.text}</p>
                      <p className="text-xs text-slate-400 mt-1">{fmtDate(c.created_at)}{c.edited_at ? " · upravené" : ""}</p>
                    </div>
                    <button onClick={() => delComm(c.id)} data-testid={`comm-delete-${c.id}`} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <textarea data-testid="comm-input" value={newComm} onChange={(e) => setNewComm(e.target.value)} rows={2}
                placeholder="Pridať interný záznam…"
                className="flex-1 px-3 py-2 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand resize-y" />
              <button data-testid="comm-add" onClick={addComm} disabled={addingComm || !newComm.trim()}
                className="btn-lift inline-flex items-center gap-1.5 bg-navy hover:bg-navy/90 text-white font-medium px-3 rounded-md text-sm disabled:opacity-50 shrink-0">
                {addingComm ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquarePlus className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Completion email */}
          {status === "completed" && (
            <div className="border-t border-navy/10 pt-4" data-testid="completion-email-section">
              <p className="text-slate-500 mb-2">E-mail o dokončení</p>
              {!item.email ? (
                <p data-testid="completion-no-email" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">Zákazník nezadal e-mailovú adresu.</p>
              ) : emailSent ? (
                <div className="space-y-2">
                  <p data-testid="completion-already-sent" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
                    E-mail zákazníkovi už bol odoslaný dňa {fmtDate(emailSentAt)}.
                  </p>
                  <button data-testid="resend-completion-email" onClick={() => setConfirmEmail(true)} className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline font-medium">
                    <Send className="w-4 h-4" /> Odoslať znova
                  </button>
                </div>
              ) : (
                <button data-testid="send-completion-email" onClick={() => setConfirmEmail(true)}
                  className="btn-lift inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2.5 rounded-md text-sm">
                  <Send className="w-4 h-4" /> Odoslať zákazníkovi e-mail
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center p-6 border-t border-navy/10">
          <button data-testid="request-delete" onClick={del} className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 font-medium">
            <Trash2 className="w-4 h-4" /> Zmazať
          </button>
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-navy">Zavrieť</button>
        </div>
      </div>

      {confirmEmail && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4" data-testid="email-confirm-modal">
          <div className="w-full max-w-sm bg-white text-navy rounded-2xl p-6 shadow-2xl">
            <p className="text-sm mb-6">Naozaj chcete odoslať zákazníkovi e-mail, že požiadavka bola dokončená?</p>
            <div className="flex gap-3 justify-end">
              <button data-testid="email-confirm-cancel" onClick={() => setConfirmEmail(false)} className="px-4 py-2.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Zrušiť</button>
              <button data-testid="email-confirm-send" disabled={sendingEmail} onClick={sendCompletionEmail}
                className="btn-lift inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-2.5 rounded-md text-sm disabled:opacity-60">
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : "Odoslať e-mail"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RequestsPanel({ token }) {
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const exportXlsx = async (mode) => {
    setExportOpen(false);
    const params = {};
    if (mode === "filtered") { params.view = view; params.search = debounced; }
    if (mode === "selected") {
      if (!selectedIds.size) { toast.error("Nie sú vybraté žiadne záznamy na export."); return; }
      params.ids = [...selectedIds].join(",");
    }
    try {
      await downloadXlsx(axios, `${API}/admin/requests/export`, params, token, `service-requests-export-${todayStr()}.xlsx`);
      toast.success("Export bol vytvorený.");
    } catch { toast.error("Export zlyhal."); }
  };

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [view, debounced, pageSize, sortBy, sortDir]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir, view, search: debounced };
    axios.get(`${API}/admin/requests`, { ...authHeader, params })
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .catch(() => toast.error("Načítanie požiadaviek zlyhalo."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortDir, view, debounced]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir(key === "date" ? "desc" : "asc"); }
  };
  const toggleId = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((it) => selectedIds.has(it.id));
  const toggleAll = () => setSelectedIds((s) => {
    const n = new Set(s);
    if (allSelected) items.forEach((it) => n.delete(it.id)); else items.forEach((it) => n.add(it.id));
    return n;
  });

  const bulk = async (action) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(`Naozaj chcete odstrániť ${ids.length} označených požiadaviek?\nTáto akcia je nevratná.`)) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/admin/requests/bulk`, { ids, action }, authHeader);
      setSelectedIds(new Set());
      toast.success(action === "delete" ? "Označené požiadavky zmazané" : action === "archive" ? "Označené požiadavky archivované" : "Označené požiadavky obnovené");
      load();
    } catch { toast.error("Hromadná akcia zlyhala."); }
    finally { setBulkBusy(false); }
  };

  const SortHead = ({ col }) => (
    <th className="px-4 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort(col.key)} data-testid={`sort-${col.key}`}>
      <span className="inline-flex items-center gap-1">{col.label}
        {sortBy === col.key && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  return (
    <main data-testid="requests-panel">
      <div className="mb-6">
        <h1 className="font-head text-2xl font-semibold">Servisné požiadavky</h1>
        <p className="text-white/60 text-sm mt-1">Kliknutím na riadok zobrazíte detail. Kliknutím na hlavičku stĺpca zoradíte.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="requests-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Hľadať podľa čísla, mena, telefónu alebo e-mailu…"
            className="w-full pl-10 pr-4 py-2.5 rounded-md bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand" />
        </div>
        <select data-testid="requests-filter" value={view} onChange={(e) => setView(e.target.value)}
          className="px-4 py-2.5 rounded-md bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-brand">
          {VIEWS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
        <div className="relative">
          <button data-testid="requests-export-btn" onClick={() => setExportOpen((o) => !o)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/5 border border-white/10 text-white hover:bg-white/10 whitespace-nowrap">
            <FileSpreadsheet className="w-4 h-4 text-green-300" /> Export XLSX <ChevronDown className="w-4 h-4" />
          </button>
          {exportOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
              <div className="absolute right-0 mt-1 z-20 w-56 bg-[#0d1420] border border-white/15 rounded-md shadow-xl py-1 text-sm">
                <button data-testid="export-all" onClick={() => exportXlsx("all")} className="block w-full text-left px-4 py-2 hover:bg-white/10">Exportovať všetky</button>
                <button data-testid="export-filtered" onClick={() => exportXlsx("filtered")} className="block w-full text-left px-4 py-2 hover:bg-white/10">Exportovať filtrované</button>
                <button data-testid="export-selected" onClick={() => exportXlsx("selected")} className="block w-full text-left px-4 py-2 hover:bg-white/10">Exportovať označené ({selectedIds.size})</button>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-white/5 border border-white/10 rounded-lg px-4 py-3" data-testid="bulk-action-bar">
          <span className="text-sm text-white/70">Označené: <b className="text-white">{selectedIds.size}</b></span>
          <div className="flex flex-wrap gap-2 ml-auto">
            <button data-testid="bulk-archive" disabled={bulkBusy} onClick={() => bulk("archive")} className="inline-flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md disabled:opacity-60"><Archive className="w-4 h-4" /> Archivovať označené</button>
            <button data-testid="bulk-unarchive" disabled={bulkBusy} onClick={() => bulk("unarchive")} className="inline-flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-md disabled:opacity-60"><ArchiveRestore className="w-4 h-4" /> Obnoviť označené</button>
            <button data-testid="bulk-delete" disabled={bulkBusy} onClick={() => bulk("delete")} className="inline-flex items-center gap-1.5 text-sm bg-red-600/90 hover:bg-red-600 text-white px-3 py-1.5 rounded-md disabled:opacity-60"><Trash2 className="w-4 h-4" /> Vymazať označené</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-white/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-xl py-20 text-center text-white/50">Žiadne požiadavky.</div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/60 text-left">
                <tr>
                  <th className="px-4 py-3 w-10"><input data-testid="select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-brand cursor-pointer" /></th>
                  {SORT_COLS.slice(0, 1).map((c) => <SortHead key={c.key} col={c} />)}
                  <SortHead col={SORT_COLS[1]} />
                  <SortHead col={SORT_COLS[2]} />
                  <th className="px-4 py-3 font-medium">Telefón</th>
                  <th className="px-4 py-3 font-medium">Služba</th>
                  <SortHead col={SORT_COLS[3]} />
                  <SortHead col={SORT_COLS[4]} />
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id} data-testid={`request-row-${i}`} onClick={() => setSelected(it)}
                    className={`border-t border-white/5 hover:bg-white/5 cursor-pointer ${it.archived ? "opacity-60" : ""}`}>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input data-testid={`request-select-${i}`} type="checkbox" checked={selectedIds.has(it.id)} onChange={() => toggleId(it.id)} className="w-4 h-4 accent-brand cursor-pointer" /></td>
                    <td className="px-4 py-3 font-mono text-brand whitespace-nowrap">{it.request_number}{it.archived && <span className="ml-2 text-[10px] text-white/40">(archív)</span>}</td>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                    <td className="px-4 py-3">{it.name}</td>
                    <td className="px-4 py-3 text-white/70">{it.phone}</td>
                    <td className="px-4 py-3 text-white/70">{it.service_type}</td>
                    <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                    <td className="px-4 py-3 whitespace-nowrap"><DeadlineDot item={it} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm text-white/70 px-1"><input data-testid="select-all-mobile" type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-brand" /> Označiť všetky</label>
            {items.map((it, i) => (
              <div key={it.id} data-testid={`request-card-${i}`} className={`bg-white/5 border border-white/10 rounded-xl p-4 ${it.archived ? "opacity-60" : ""}`}>
                <div className="flex items-center gap-3 mb-2">
                  <input data-testid={`request-select-mobile-${i}`} type="checkbox" checked={selectedIds.has(it.id)} onChange={() => toggleId(it.id)} className="w-4 h-4 accent-brand" />
                  <button onClick={() => setSelected(it)} className="flex-1 flex items-center justify-between text-left">
                    <span className="font-mono text-brand text-sm">{it.request_number}</span>
                    <StatusBadge status={it.status} />
                  </button>
                </div>
                <button onClick={() => setSelected(it)} className="text-left w-full">
                  <p className="font-medium">{it.name}</p>
                  <p className="text-white/60 text-sm">{it.phone} · {it.service_type}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-white/40 text-xs">{fmtDate(it.created_at)}</p>
                    <DeadlineDot item={it} />
                  </div>
                </button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-5">
            <div className="flex items-center gap-2 text-sm text-white/60">
              <span>Na stranu:</span>
              <select data-testid="page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-brand">
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="ml-2">Spolu: {total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-2 rounded-md bg-white/5 border border-white/10 disabled:opacity-40 hover:bg-white/10"><ChevronLeft className="w-4 h-4" /></button>
              <span data-testid="page-info" className="text-sm text-white/70">Strana {page} z {totalPages}</span>
              <button data-testid="page-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="p-2 rounded-md bg-white/5 border border-white/10 disabled:opacity-40 hover:bg-white/10"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </>
      )}

      {selected && <RequestDetailModal item={selected} token={token} onClose={() => setSelected(null)} onChanged={load} />}
    </main>
  );
}
